import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";
import {
  businessThisWeekRange,
  businessToday,
  businessTodayRange,
  businessYesterdayRange,
  businessMonthToDateRange,
} from "src/utils/businessDay";
import { localISODate } from "src/utils/localDate";

// Informe de promociones horarias y suplementos por zona (P4).
//
// API: GET analytics/promotions?desde=yyyy-MM-dd&hasta=yyyy-MM-dd
//        [&priceRuleId=..][&zoneChargeId=..]
//   → { desde, hasta, rules:[…], zoneCharges:[…], totals:{…} }
//
// Las fechas son FECHAS DE NEGOCIO (el API aplica la hora de inicio de
// jornada). Todo el parseo es defensivo (camelCase/PascalCase, nulos).

const initialState = {
  loading: false,
  error: null,
  report: null,
  // Última petición resuelta (para no pintar datos de otro rango).
  requestKey: null,
};

const slice = createSlice({
  name: "promotionsReport",
  initialState,
  reducers: {
    startLoading(state, action) {
      state.loading = true;
      state.error = null;
      state.requestKey = action.payload ?? null;
    },
    hasError(state, action) {
      state.loading = false;
      state.error = action.payload || "Error";
    },
    setReport(state, action) {
      state.loading = false;
      state.report = action.payload;
    },
    clear() {
      return initialState;
    },
  },
});

export default slice.reducer;
export const { clear: clearPromotionsReport } = slice.actions;

const asArray = (v) => (Array.isArray(v) ? v : []);
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const pick = (o, a, b) => (o?.[a] !== undefined ? o[a] : o?.[b]);

/** Array de longitud fija (rellena con 0 lo que falte). */
function fixed(arr, len) {
  const src = asArray(arr);
  return Array.from({ length: len }, (_, i) => num(src[i]));
}

function normalizeRuleRow(r) {
  if (!r || typeof r !== "object") return null;
  const revenue = num(pick(r, "revenue", "Revenue"));
  const catalogRevenue = num(pick(r, "catalogRevenue", "CatalogRevenue"));
  const discountRaw = pick(r, "discount", "Discount");
  const discount = discountRaw == null ? Math.max(0, catalogRevenue - revenue) : num(discountRaw);
  const pctRaw = pick(r, "avgDiscountPct", "AvgDiscountPct");
  const avgDiscountPct =
    pctRaw == null ? (catalogRevenue > 0 ? (discount / catalogRevenue) * 100 : 0) : num(pctRaw);
  return {
    priceRuleId: pick(r, "priceRuleId", "PriceRuleId") ?? null,
    name: pick(r, "name", "Name") || "(promoción eliminada)",
    lines: num(pick(r, "lines", "Lines")),
    units: num(pick(r, "units", "Units")),
    revenue,
    catalogRevenue,
    discount,
    avgDiscountPct,
    byWeekday: fixed(pick(r, "byWeekday", "ByWeekday"), 7),
    byHour: fixed(pick(r, "byHour", "ByHour"), 24),
    topProducts: asArray(pick(r, "topProducts", "TopProducts")).map((p) => ({
      productId: pick(p, "productId", "ProductId") ?? null,
      name: pick(p, "name", "Name") || "—",
      units: num(pick(p, "units", "Units")),
      discount: num(pick(p, "discount", "Discount")),
    })),
  };
}

function normalizeZoneRow(z) {
  if (!z || typeof z !== "object") return null;
  return {
    zoneChargeId: pick(z, "zoneChargeId", "ZoneChargeId") ?? null,
    name: pick(z, "name", "Name") || "(suplemento eliminado)",
    zonaId: pick(z, "zonaId", "ZonaId") ?? null,
    zonaName: pick(z, "zonaName", "ZonaName") || "",
    mode: String(pick(z, "mode", "Mode") ?? "").toUpperCase(),
    lines: num(pick(z, "lines", "Lines")),
    units: num(pick(z, "units", "Units")),
    amount: num(pick(z, "amount", "Amount")),
    tickets: num(pick(z, "tickets", "Tickets")),
  };
}

/** Normaliza la respuesta del API. Calcula totales si faltan. */
export function normalizePromotionsReport(raw) {
  const d = raw && typeof raw === "object" ? raw : {};
  const rules = asArray(pick(d, "rules", "Rules")).map(normalizeRuleRow).filter(Boolean);
  const zoneCharges = asArray(pick(d, "zoneCharges", "ZoneCharges")).map(normalizeZoneRow).filter(Boolean);
  const t = pick(d, "totals", "Totals") || {};
  const sum = (arr, k) => arr.reduce((s, x) => s + x[k], 0);
  const val = (a, b, fallback) => {
    const v = pick(t, a, b);
    return v == null ? fallback : num(v);
  };
  const promoRevenue = val("promoRevenue", "PromoRevenue", sum(rules, "revenue"));
  const revenueTotal = val("revenueTotal", "RevenueTotal", 0);
  return {
    desde: pick(d, "desde", "Desde") ?? null,
    hasta: pick(d, "hasta", "Hasta") ?? null,
    rules,
    zoneCharges,
    totals: {
      promoDiscount: val("promoDiscount", "PromoDiscount", sum(rules, "discount")),
      promoRevenue,
      promoUnits: val("promoUnits", "PromoUnits", sum(rules, "units")),
      zoneChargeAmount: val("zoneChargeAmount", "ZoneChargeAmount", sum(zoneCharges, "amount")),
      revenueTotal,
      promoShareOfRevenuePct: val(
        "promoShareOfRevenuePct",
        "PromoShareOfRevenuePct",
        revenueTotal > 0 ? (promoRevenue / revenueTotal) * 100 : 0,
      ),
    },
  };
}

/** true si el informe no tiene ni promociones ni suplementos con datos. */
export function isPromotionsReportEmpty(report) {
  if (!report) return true;
  const rules = asArray(report.rules).filter((r) => r.units > 0 || r.lines > 0 || r.discount !== 0);
  const zones = asArray(report.zoneCharges).filter((z) => z.amount !== 0 || z.units > 0 || z.tickets > 0);
  return rules.length === 0 && zones.length === 0;
}

function addDays(d, n) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/**
 * Presets en DÍAS DE NEGOCIO (`h` = hora de inicio de jornada; `now` para
 * tests). «Esta semana» = lunes → hoy; «Este mes» = día 1 → hoy.
 */
export const PROMO_RANGE_PRESETS = {
  today: (h, now) => ({ ...businessTodayRange(h, now), label: "Hoy" }),
  yesterday: (h, now) => ({ ...businessYesterdayRange(h, now), label: "Ayer" }),
  week: (h, now) => {
    const { desde } = businessThisWeekRange(h, now);
    const today = businessToday(now ?? new Date(), h);
    return { desde, hasta: today < addDays(desde, 6) ? today : addDays(desde, 6), label: "Esta semana" };
  },
  month: (h, now) => ({ ...businessMonthToDateRange(h, now), label: "Este mes" }),
};

function fmt(d) {
  if (!d) return null;
  if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return localISODate(dt);
}

/** Parámetros de la petición (exportado para tests). */
export function buildPromotionsReportParams({ desde, hasta, priceRuleId, zoneChargeId } = {}) {
  const params = { desde: fmt(desde), hasta: fmt(hasta) };
  if (priceRuleId != null && priceRuleId !== "") params.priceRuleId = priceRuleId;
  if (zoneChargeId != null && zoneChargeId !== "") params.zoneChargeId = zoneChargeId;
  return params;
}

/** Carga el informe. Ignora respuestas de peticiones ya superadas. */
export function fetchPromotionsReport(args = {}) {
  return async (dispatch, getState) => {
    const params = buildPromotionsReportParams(args);
    if (!params.desde || !params.hasta) return null;
    const key = JSON.stringify(params);
    dispatch(slice.actions.startLoading(key));
    const stillCurrent = () => {
      const s = typeof getState === "function" ? getState()?.promotionsReport : null;
      return !s || s.requestKey === key;
    };
    try {
      const res = await axios.get("/analytics/promotions", { params });
      const report = normalizePromotionsReport(res?.data);
      if (stillCurrent()) dispatch(slice.actions.setReport(report));
      return report;
    } catch (e) {
      if (stillCurrent()) {
        dispatch(slice.actions.hasError(
          e?.response?.data?.message || e?.message || "No se pudo cargar el informe.",
        ));
      }
      return null;
    }
  };
}
